import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, OnDestroy, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { env } from '../env.generated';

export interface AppVersionInfo {
  buildId: string;
  missionId?: string;
  builtAt?: string;
}

export const APP_BUILD_ID = new InjectionToken<string>('APP_BUILD_ID', {
  providedIn: 'root',
  factory: () => env.NG_APP_BUILD_ID,
});

export const APP_RELOAD = new InjectionToken<() => void>('APP_RELOAD', {
  providedIn: 'root',
  factory: () => {
    const doc = inject(DOCUMENT);
    return () => doc.defaultView?.location.reload();
  },
});

export const APP_UPDATE_CHECK_INTERVAL_MS = 2 * 60 * 1000;
export const APP_UPDATE_SNOOZE_MS = 15 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly currentBuildId = inject(APP_BUILD_ID);
  private readonly reloadPage = inject(APP_RELOAD);

  readonly updateAvailable = signal(false);
  readonly dialogVisible = signal(false);
  readonly reloading = signal(false);
  readonly latestVersion = signal<AppVersionInfo | null>(null);

  private started = false;
  private checking = false;
  private snoozedUntil = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;

  private readonly onFocus = () => void this.check();
  private readonly onOnline = () => void this.check();
  private readonly onVisibilityChange = () => {
    if (this.document.visibilityState === 'visible') {
      this.snoozedUntil = 0;
      void this.check();
    }
  };

  start(): void {
    if (this.started || !this.currentBuildId) return;
    this.started = true;

    const win = this.document.defaultView;
    win?.addEventListener('focus', this.onFocus);
    win?.addEventListener('online', this.onOnline);
    this.document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.intervalId = setInterval(() => void this.check(), APP_UPDATE_CHECK_INTERVAL_MS);

    void this.check();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;

    const win = this.document.defaultView;
    win?.removeEventListener('focus', this.onFocus);
    win?.removeEventListener('online', this.onOnline);
    this.document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  async check(): Promise<void> {
    if (this.updateAvailable()) {
      this.showIfDue();
      return;
    }
    if (this.checking || !this.currentBuildId) return;

    this.checking = true;
    try {
      const info = await firstValueFrom(
        this.http.get<AppVersionInfo>(`version.json?t=${Date.now()}`)
      );
      if (info?.buildId && info.buildId !== this.currentBuildId) {
        this.latestVersion.set(info);
        this.updateAvailable.set(true);
        this.showIfDue();
      }
    } catch {
      // Sin red o version.json no disponible: se reintenta en la siguiente comprobación.
    } finally {
      this.checking = false;
    }
  }

  postpone(): void {
    if (this.reloading() || !this.dialogVisible()) return;
    this.dialogVisible.set(false);
    this.snoozedUntil = Date.now() + APP_UPDATE_SNOOZE_MS;
  }

  applyUpdate(): void {
    if (this.reloading()) return;
    this.reloading.set(true);
    this.reloadPage();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private showIfDue(): void {
    if (!this.dialogVisible() && Date.now() >= this.snoozedUntil) {
      this.dialogVisible.set(true);
    }
  }
}
